/*!
 * index.js — интерфейс /destiny-matrix.
 *
 * Расчёт живёт в engine.js и проверен сторожем на числах двух живых
 * калькуляторов; здесь только показ. Разделение не косметическое: арифметику
 * этого метода нельзя «поправить на глаз», и держать её отдельно от DOM —
 * единственный способ проверять её без браузера.
 *
 * Решения, которые стоит объяснить:
 *
 * 1. Результат приходит СРАЗУ, без кнопки «рассчитать», как только дата
 *    полная. Кнопка есть (без неё непонятно, что делать), но она уже не
 *    единственный путь: замер чужих калькуляторов показал, что половина
 *    экрана до нажатия — это форма и три блока рекламы.
 *
 * 2. Каждое число объясняется цепочкой сложения. Это главный ответ на
 *    «откуда вы это взяли»: единственная проверяемая часть метода — его
 *    арифметика, и прятать её значит оставлять себе только веру.
 *
 * 3. «Вы здесь» на кольце возрастов. Кольцо есть у всех, но подписаны только
 *    десятилетия; какой аркан работает СЕЙЧАС и до какой даты — не говорит
 *    никто, хотя это ровно то, зачем человек и пришёл.
 *
 * 4. Всё, что есть на схеме, продублировано таблицами. Схема на телефоне
 *    мельче читаемого предела, и «зумьте» — не ответ: таблица и есть ответ,
 *    а заодно её читает скринридер и берёт печать.
 */
import { compute, combine, arcanaAtAge, ringTimeline, ageOn, explain, explainPurpose } from './engine.js';
import { render as renderChart, fmtAge } from './chart.js';

const T = window.__DM_I18N || {};
const t = (k, fb) => (T[k] !== undefined ? T[k] : (fb !== undefined ? fb : k));
const $ = (id) => document.getElementById(id);
const SLUG = 'destiny-matrix';

let ARCANA = null;      // {1:{name,essence,...}, ...}
let CHART = null;       // текущий расчёт
let SELECTED = null;    // выбранная точка схемы
let RING_ON = true;
let PAIR = null;        // матрица пары, если введена вторая дата

/* ── арканы ───────────────────────────────────────────────────────────── */

async function loadArcana() {
    if (ARCANA) return ARCANA;
    const urls = (window.__DM_ARCANA_URLS || []).slice();
    for (const url of urls) {
        try {
            const r = await fetch(url, { credentials: 'same-origin' });
            if (!r.ok) continue;
            const j = await r.json();
            if (j && j.arcana && j.arcana['1']) { ARCANA = j.arcana; return ARCANA; }
        } catch (e) { /* следующий кандидат */ }
    }
    // Без толкований тул всё равно считает и рисует — это честнее пустого
    // экрана, и заметно: подписи станут «Аркан N».
    window.__aeFunnel && window.__aeFunnel('arcana_load_failed', { tried: urls.length });
    ARCANA = {};
    return ARCANA;
}
const arc = (n) => (ARCANA && ARCANA[n]) || null;
const arcName = (n) => {
    const a = arc(n);
    return a ? n + '. ' + a.name : t('arcana_n', 'Аркан {n}').replace('{n}', n);
};

/* ── разметка ─────────────────────────────────────────────────────────── */

const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Подписи точек для схемы, тултипов и aria. */
function pointLabels() {
    const L = {};
    for (const k of ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'S', 'O', 'W', 'N', 'Q',
        'T', 'P', 'X', 'J', 'R', 'U', 'F1', 'F2', 'G1', 'G2', 'H1', 'H2', 'I1', 'I2']) {
        L['pt_' + k] = t('pt_' + k, k);
    }
    L.chart_aria = t('chart_aria', 'Схема матрицы судьбы');
    L.ring_node = t('ring_node', '{age} лет — аркан {n}');
    L.age_here = t('age_here', 'Вы здесь');
    return L;
}

/* ── отрисовка ────────────────────────────────────────────────────────── */

/**
 * Вынос коробки схемы за поля панели на узком экране.
 *
 * Считается ЗАМЕРОМ, а не константой: между схемой и краем экрана лежат два
 * отступа — панели тула и контейнера страницы, — и второй нам не принадлежит.
 * На 320 px эти 34 px были ровно той разницей, из-за которой цифры выходили
 * 10.8 px вместо 12.
 * ⛔ `width:100vw` вместо этого не годится: в узком окне десктопа 100vw шире
 * клиентской области на полосу прокрутки, и страница начинает ехать вбок.
 */
function fitChartBleed() {
    const boxes = document.querySelectorAll('.dm-chart-box');
    const wide = !window.matchMedia('(max-width: 560px)').matches;
    boxes.forEach((box) => {
        box.style.removeProperty('--dm-bleed');
        if (wide) return;
        const avail = document.documentElement.clientWidth;
        const own = box.getBoundingClientRect().width;
        const bleed = Math.floor((avail - own) / 2);
        if (bleed > 0) box.style.setProperty('--dm-bleed', bleed + 'px');
    });
}

function drawChart() {
    const host = $('dm-chart');
    if (!host || !CHART) return;
    host.textContent = '';
    host.classList.toggle('is-tight', !RING_ON);
    const age = currentAge();
    const svg = renderChart(CHART, {
        ring: RING_ON,
        ageMark: age !== null && age < 80 ? age : null,
        labels: pointLabels(),
        selected: SELECTED,
        onPick: (key) => selectPoint(key),
    });
    host.appendChild(svg);
    fitChartBleed();
}

/** Возраст на сегодня или null, если дата в будущем / вне шкалы. */
function currentAge() {
    if (!CHART) return null;
    const b = new Date(Date.UTC(CHART.input.year, CHART.input.month - 1, CHART.input.day));
    const a = ageOn(b, new Date());
    return a >= 0 ? a : null;
}

/** Дата, когда закончится текущий период кольца. */
function periodEndDate(toAge) {
    const b = new Date(Date.UTC(CHART.input.year, CHART.input.month - 1, CHART.input.day));
    return new Date(b.getTime() + toAge * 365.2425 * 24 * 3600 * 1000);
}

const fmtDate = (d) => d.toLocaleDateString(window.__LOCALE || 'ru', { year: 'numeric', month: 'long' });

function selectPoint(key) {
    SELECTED = key;
    drawChart();
    const box = $('dm-detail');
    if (!box) return;
    const n = CHART.points[key];
    const a = arc(n);
    const ex = explain(CHART, key);
    box.innerHTML =
        '<div class="dm-detail-head"><span class="dm-detail-pt">' + esc(t('pt_' + key, key)) + '</span>'
        + '<strong class="dm-detail-arc">' + esc(arcName(n)) + '</strong></div>'
        + (a ? '<p class="dm-detail-essence">' + esc(a.essence) + '</p>' : '')
        + (a ? '<p>' + esc(pointText(key, a)) + '</p>' : '')
        + formulaHtml(ex, t('pt_' + key, key));
    box.hidden = false;
    // block:'nearest' — иначе страница прыгает под пальцем при каждом тапе
    box.scrollIntoView({ block: 'nearest' });
    // Раскрытие арифметики — то, ради чего тул и написан. Без замера мы не
    // узнаем, читает ли её кто-нибудь, и стоит ли выносить её из-под ката.
    const det = box.querySelector('.dm-formula');
    if (det && !det._wired) {
        det._wired = true;
        det.addEventListener('toggle', () => {
            if (det.open) window.__aeFunnel && window.__aeFunnel('formula_open', { point: key });
        });
    }
    window.__aeFunnel && window.__aeFunnel('point_open', { point: key, arcana: n });
}

/** Какое из полей аркана уместно на этой позиции. */
function pointText(key, a) {
    if (key === 'N' || key === 'Q') return a.money;
    if (key === 'W' || key === 'S') return a.love;
    if (key === 'D' || key === 'J' || key === 'R') return a.karma;
    if (/^[FGHI]\d?$/.test(key) || key === 'U') return a.clan;
    if (key === 'A' || key === 'E') return a.gift;
    return a.gift;
}

function formulaHtml(ex, title) {
    if (!ex) return '';
    const names = { day: t('f_day', 'день'), month: t('f_month', 'месяц'), year: t('f_year', 'год') };
    const chain = ex.parts.map((p) => {
        const label = names[p.key] || t('pt_' + p.key, p.key);
        const val = p.raw !== undefined && p.raw !== p.value
            ? p.raw + ' → ' + p.value
            : String(p.value);
        return '<span class="dm-f-part"><em>' + esc(label) + '</em> ' + esc(val) + '</span>';
    }).join('<span class="dm-f-op">+</span>');
    const tail = ex.reduced
        ? ' = ' + ex.sum + ' → <b>' + ex.result + '</b>'
        : ' = <b>' + ex.result + '</b>';
    return '<details class="dm-formula"><summary>' + esc(t('how_counted', 'Как получено это число'))
        + '</summary><div class="dm-f-chain">' + chain + tail + '</div>'
        + (ex.reduced ? '<p class="dm-f-note">' + esc(t('reduce_note',
            'Сумма больше 22 сворачивается сложением цифр, пока не станет арканом от 1 до 22.')) + '</p>' : '')
        + '</details>';
}

/* ── блоки результата ─────────────────────────────────────────────────── */

function purposeRow(key) {
    const v = CHART.purpose[key];
    const ex = explainPurpose(CHART, key);
    const parts = ex ? ex.parts.map(p => p.value).join(' + ') : '';
    return '<tr><th scope="row">' + esc(t('pp_' + key, key)) + '</th>'
        + '<td class="dm-num">' + v + '</td>'
        + '<td class="dm-arc">' + esc(arcName(v)) + '</td>'
        + '<td class="dm-src">' + esc(parts) + '</td></tr>';
}

function renderPurpose() {
    const box = $('dm-purpose');
    if (!box) return;
    box.innerHTML =
        '<table class="dm-table"><caption class="dm-sr">' + esc(t('sec_purpose', 'Предназначение')) + '</caption>'
        + '<thead><tr><th>' + esc(t('th_line', 'Линия')) + '</th><th>' + esc(t('th_arcana', 'Аркан'))
        + '</th><th>' + esc(t('th_meaning', 'Значение')) + '</th><th>' + esc(t('th_from', 'Из чего')) + '</th></tr></thead><tbody>'
        + ['sky', 'earth', 'personal', 'male', 'female', 'social', 'spiritual', 'planetary'].map(purposeRow).join('')
        + '</tbody></table>';
}

const CHAKRAS = ['sahasrara', 'ajna', 'vishuddha', 'anahata', 'manipura', 'svadhisthana', 'muladhara'];

function renderChakras() {
    const box = $('dm-chakras');
    if (!box || !CHART) return;
    const c = CHART.chakras;
    const rows = CHAKRAS.map((name, i) =>
        '<tr><th scope="row"><span class="dm-ch-name">' + esc(t('ch_' + name, name)) + '</span>'
        + '<span class="dm-ch-sub">' + esc(t('ch_' + name + '_sub', '')) + '</span></th>'
        + '<td class="dm-num">' + c.physics[i] + '</td>'
        + '<td class="dm-num">' + c.energy[i] + '</td>'
        + '<td class="dm-num">' + c.emotion[i] + '</td></tr>').join('');
    box.innerHTML =
        '<table class="dm-table dm-table-chakra"><thead><tr><th>' + esc(t('th_chakra', 'Чакра')) + '</th>'
        + '<th>' + esc(t('th_physics', 'Физика')) + '</th><th>' + esc(t('th_energy', 'Энергия'))
        + '</th><th>' + esc(t('th_emotion', 'Эмоции')) + '</th></tr></thead>'
        + '<tbody>' + rows + '</tbody>'
        + '<tfoot><tr><th scope="row">' + esc(t('th_total', 'Итого')) + '</th>'
        + '<td class="dm-num">' + c.totals.physics + '</td><td class="dm-num">' + c.totals.energy
        + '</td><td class="dm-num">' + c.totals.emotion + '</td></tr></tfoot></table>'
        + '<p class="dm-hint">' + esc(t('chakra_hint',
            'Физика — горизонтальная ось схемы, энергия — вертикальная, эмоции — их сумма.')) + '</p>';
}

function renderNow() {
    const box = $('dm-now');
    if (!box || !CHART) return;
    const age = currentAge();
    if (age === null) {
        // Дата ещё не наступила — считать «что работает сейчас» не от чего.
        // Молча прятать блок нельзя: он только что был, и его исчезновение
        // читается как поломка, а не как ответ.
        box.hidden = false;
        box.innerHTML = '<p class="dm-hint">' + esc(t('age_future',
            'Дата ещё не наступила — возрастная шкала начнёт работать с неё.')) + '</p>';
        return;
    }
    if (age >= 80) {
        // Возраст за пределами шкалы метода: если таких посетителей много,
        // «до 80 лет» перестаёт быть деталью и становится ограничением тула.
        window.__aeFunnel && window.__aeFunnel('age_over_scale', { age: Math.round(age) });
        box.hidden = false;
        box.innerHTML = '<p class="dm-hint">' + esc(t('age_over',
            'Возрастная шкала метода заканчивается на 80 годах — дальше карта её не считает.')) + '</p>';
        return;
    }
    const at = arcanaAtAge(CHART, age);
    const a = arc(at.arcana);
    const end = periodEndDate(at.toAge);
    box.hidden = false;
    box.innerHTML =
        '<div class="dm-now-head">' + esc(t('now_title', 'Что работает сейчас')) + '</div>'
        + '<div class="dm-now-arc">' + esc(arcName(at.arcana)) + '</div>'
        + '<div class="dm-now-period">' + esc(t('now_period', 'Период {from}–{to} лет, до {date}')
            .replace('{from}', fmtAge(at.fromAge)).replace('{to}', fmtAge(at.toAge))
            .replace('{date}', fmtDate(end))) + '</div>'
        + (a ? '<p>' + esc(a.essence) + ' ' + esc(a.gift) + '</p>' : '');
}

function renderTimeline() {
    const box = $('dm-timeline');
    if (!box || !CHART) return;
    const age = currentAge();
    const rows = ringTimeline(CHART).map((n) => {
        const isNow = age !== null && age >= n.fromAge && age < n.toAge;
        return '<tr' + (isNow ? ' class="is-now"' : '') + '><th scope="row">'
            + fmtAge(n.fromAge) + '–' + fmtAge(n.toAge) + '</th>'
            + '<td class="dm-num">' + n.arcana + '</td>'
            + '<td>' + esc(arcName(n.arcana)) + '</td></tr>';
    }).join('');
    box.innerHTML = '<table class="dm-table dm-table-timeline"><thead><tr><th>'
        + esc(t('th_age', 'Возраст')) + '</th><th>' + esc(t('th_arcana', 'Аркан'))
        + '</th><th>' + esc(t('th_meaning', 'Значение')) + '</th></tr></thead><tbody>' + rows + '</tbody></table>';
}

function renderReading() {
    const box = $('dm-reading');
    if (!box || !CHART) return;
    const p = CHART.points;
    const block = (titleKey, n, field, why) => {
        const a = arc(n);
        return '<section class="dm-read"><h3>' + esc(t(titleKey, titleKey)) + '</h3>'
            + '<div class="dm-read-arc">' + esc(arcName(n)) + '</div>'
            + (why ? '<p class="dm-read-why">' + esc(why) + '</p>' : '')
            + (a ? '<p>' + esc(a[field]) + '</p>' : '')
            + (a && field !== 'shadow' ? '<p class="dm-read-shadow"><b>' + esc(t('shadow_label', 'Теневая сторона'))
                + '.</b> ' + esc(a.shadow) + '</p>' : '')
            + '</section>';
    };
    box.innerHTML = [
        block('read_center', p.E, 'gift', t('why_center', 'Центр схемы — ядро характера.')),
        block('read_person', p.A, 'gift', t('why_person', 'Аркан по дню рождения.')),
        block('read_talent', p.B, 'gift', t('why_talent', 'Аркан по месяцу рождения.')),
        block('read_karma', p.D, 'karma', t('why_karma', 'Сумма дня, месяца и года.')),
        block('read_money', p.N, 'money', t('why_money', 'Денежный канал — точка между центром и правой вершиной.')),
        block('read_love', p.W, 'love', t('why_love', 'Сердечный канал — точка между центром и левой вершиной.')),
        block('read_male', p.F, 'clan', t('why_male', 'Мужская линия рода — верхний левый угол.')),
        block('read_female', p.G, 'clan', t('why_female', 'Женская линия рода — верхний правый угол.')),
    ].join('');
}

/* ── расчёт ───────────────────────────────────────────────────────────── */

function readDate(id) {
    const v = (($(id || 'dm-date')) || {}).value || '';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (!m) return null;
    const y = +m[1], mo = +m[2], d = +m[3];
    if (y < 1900 || y > 2100 || mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    // Отсечь 31 апреля и 30 февраля: браузерный date их не даст, но ссылкой
    // с ?d= прийти можно, а печатать карту несуществующего дня — стыдно.
    const probe = new Date(Date.UTC(y, mo - 1, d));
    if (probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return null;
    return { y, mo, d };
}

function calc(fromUser) {
    const dt = readDate();
    const err = $('dm-error');
    if (!dt) {
        const raw = (($('dm-date') || {}).value || '').trim();
        if (err) {
            err.hidden = false;
            err.textContent = raw ? t('err_date', 'Проверьте дату рождения.')
                : t('err_empty', 'Укажите дату рождения.');
        }
        // Устаревший результат обязан уйти: карта от прежней даты рядом с
        // новой датой в поле читается как «вот ваша карта».
        const res = $('dm-results');
        if (res) res.hidden = true;
        CHART = null;
        if (fromUser) window.__aeFunnel && window.__aeFunnel('date_rejected', { raw: raw.slice(0, 12) });
        return;
    }
    if (err) err.hidden = true;
    CHART = compute(dt.d, dt.mo, dt.y);
    SELECTED = null;
    $('dm-results').hidden = false;
    drawChart();
    renderPurpose();
    renderChakras();
    renderNow();
    renderTimeline();
    renderReading();
    renderPair(dt);
    updateShareUrl(dt);
    const nameEl = $('dm-name');
    const who = nameEl && nameEl.value.trim();
    const cap = $('dm-caption');
    if (cap) {
        cap.textContent = (who ? who + ' · ' : '')
            + String(dt.d).padStart(2, '0') + '.' + String(dt.mo).padStart(2, '0') + '.' + dt.y;
    }
    // Сила рода считается методом, но на схеме ей нет своей точки — у неё нет
    // геометрии, это сумма четырёх углов. Показываем числом в легенде, иначе
    // значение существует в расчёте и нигде на экране.
    const power = $('dm-clan-power');
    if (power) {
        power.innerHTML = '<span class="dm-lg-power">' + esc(t('pt_U', 'Сила рода'))
            + ': <b>' + CHART.points.U + '</b></span>';
    }
    if (fromUser) {
        window.__stage && window.__stage('chart_built');
        window.__aeToolComplete && window.__aeToolComplete({ ok: true });
    }
}

/**
 * Матрица пары.
 *
 * Считается ПОТОЧЕЧНЫМ сложением двух карт, а не пересчётом от сложенных
 * вершин: проверено на живом калькуляторе, разница видна сразу (низ пары 18
 * против 9 у пересчёта). Правило живёт в engine.combine() и закреплено
 * сторожем на снятых числах.
 *
 * Кольцо возрастов у пары не рисуем: у двоих разные дни рождения, и «вы
 * здесь» на общей шкале было бы выдумкой. Считается оно тем же правилом —
 * чтобы структура карты оставалась целой, — но на экран не идёт.
 */
function renderPair(dt) {
    const box = $('dm-pair');
    if (!box) return;
    const d2 = readDate('dm-date2');
    const detail = $('dm-pair-detail');
    if (detail) { detail.hidden = true; detail.innerHTML = ''; }
    if (!d2 || !CHART) { box.hidden = true; PAIR = null; return; }

    const other = compute(d2.d, d2.mo, d2.y);
    PAIR = combine(CHART, other);
    box.hidden = false;

    const host = $('dm-pair-chart');
    host.textContent = '';
    host.appendChild(renderChart(PAIR, {
        ring: false, labels: pointLabels(),
        onPick: (key) => selectPairPoint(key),
    }));
    fitChartBleed();

    const fmt = (x) => String(x.d).padStart(2, '0') + '.' + String(x.mo).padStart(2, '0') + '.' + x.y;
    const cap = $('dm-pair-caption');
    if (cap) cap.textContent = fmt(dt) + '  +  ' + fmt(d2);

    const p = PAIR.points;
    const block = (titleKey, n, why) => {
        const a = arc(n);
        return '<section class="dm-read"><h3>' + esc(t(titleKey, titleKey)) + '</h3>'
            + '<div class="dm-read-arc">' + esc(arcName(n)) + '</div>'
            + (why ? '<p class="dm-read-why">' + esc(why) + '</p>' : '')
            + (a && a.pair ? '<p>' + esc(a.pair) + '</p>' : '')
            + '</section>';
    };
    $('dm-pair-reading').innerHTML = [
        block('pair_center', p.E, t('why_pair_center', 'Центр общей схемы — главная тема этой пары.')),
        block('pair_heart', p.W, t('why_pair_heart', 'Сердечный канал пары.')),
        block('pair_money', p.N, t('why_pair_money', 'Денежный канал пары.')),
        block('pair_karma', p.D, t('why_pair_karma', 'Общая кармическая задача.')),
    ].join('');

    window.__aeFunnel && window.__aeFunnel('pair_built', { center: p.E });
}

/**
 * Карточка точки на схеме пары.
 *
 * Карточка ОДНА и перерисовывается. Раньше каждый тап дописывал блок в начало
 * разбора (`insertAdjacentHTML('afterbegin')`), и десять тапов давали десять
 * карточек поверх четырёх постоянных: разбор пары рос без предела, а какая из
 * копий отвечает последнему нажатию — не отличить. Это тот же контракт, что у
 * одиночной схемы, где карточка всегда одна.
 */
function selectPairPoint(key) {
    if (!PAIR) return;
    const n = PAIR.points[key];
    const a = arc(n);
    const box = $('dm-pair-detail');
    if (!box) return;
    box.innerHTML =
        '<div class="dm-detail-head"><span class="dm-detail-pt">' + esc(t('pt_' + key, key)) + '</span>'
        + '<strong class="dm-detail-arc">' + esc(arcName(n)) + '</strong></div>'
        + (a && a.pair ? '<p>' + esc(a.pair) + '</p>' : '')
        + formulaPairHtml(key, n);
    box.hidden = false;
    box.scrollIntoView({ block: 'nearest' });
    window.__aeFunnel && window.__aeFunnel('pair_point_open', { point: key, arcana: n });
}

/** У пары арифметика простая и её тоже надо показать: сложение двух карт. */
function formulaPairHtml(key, result) {
    if (!CHART || !PAIR) return '';
    const d2 = readDate('dm-date2');
    if (!d2) return '';
    const a = CHART.points[key];
    const b = compute(d2.d, d2.mo, d2.y).points[key];
    const sum = a + b;
    return '<details class="dm-formula"><summary>' + esc(t('how_counted', 'Как получено это число'))
        + '</summary><div class="dm-f-chain">'
        + '<span class="dm-f-part">' + a + '</span><span class="dm-f-op">+</span>'
        + '<span class="dm-f-part">' + b + '</span>'
        + (sum !== result ? ' = ' + sum + ' → <b>' + result + '</b>' : ' = <b>' + result + '</b>')
        + '</div></details>';
}

function updateShareUrl(dt) {
    const iso = dt.y + '-' + String(dt.mo).padStart(2, '0') + '-' + String(dt.d).padStart(2, '0');
    const d2 = readDate('dm-date2');
    try {
        const u = new URL(location.href);
        u.searchParams.set('d', iso);
        if (d2) u.searchParams.set('d2', d2.y + '-' + String(d2.mo).padStart(2, '0') + '-' + String(d2.d).padStart(2, '0'));
        else u.searchParams.delete('d2');
        history.replaceState(null, '', u.toString());
    } catch (e) { /* about:srcdoc и прочая экзотика — ссылка просто не обновится */ }
}

/* ── экспорт ──────────────────────────────────────────────────────────── */

/**
 * Стили схемы для экспорта.
 *
 * Ищем по id, но НЕ полагаемся только на него: минификатор HTML однажды уже
 * выбрасывал атрибуты у инлайновых `<style>` (починено 06.09.2026), и с тех
 * пор известно, что имя блока — не гарантия. Запасной путь ищет тот же блок
 * по его собственной сигнатуре; если нет и его, экспорт честно отказывается,
 * а не отдаёт чёрный квадрат.
 */
function chartCss() {
    const byId = document.getElementById('dm-chart-css');
    if (byId && byId.textContent.trim()) return byId.textContent;
    for (const st of document.querySelectorAll('style')) {
        if (st.textContent.indexOf('.dm-svg') !== -1) return st.textContent;
    }
    return '';
}

/**
 * Копия схемы со ВПЕЧАТАННЫМИ стилями: вне страницы CSS-классов нет.
 *
 * ⚠️ Список переменных НЕ пишется руками. Он писался — и в нём не было
 * `--bg-secondary`, которым залита КАЖДАЯ точка схемы, и `--bg-tertiary` у
 * углов. Невалидный var() делает всё объявление недействительным, поэтому в
 * скачанном PNG кружки уходили в чёрный (а в светлой теме — чёрные кружки под
 * тёмными цифрами, то есть карта без чисел). На экране этого не видно: там
 * переменные есть. Теперь имена вынимаются из самого текста стилей — забыть
 * новую переменную нельзя, её принесёт та же строка, что её и объявила.
 */
function standaloneSvg() {
    const src = $('dm-chart').querySelector('svg');
    if (!src) return null;
    const clone = src.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    /* Холст берётся из viewBox, а не из константы 1000: без кольца схема
       рисуется в обрезанном холсте, и жёсткая тысяча растягивала бы её
       произвольно и клала фон мимо. */
    const vb = (clone.getAttribute('viewBox') || '0 0 1000 1000').split(/\s+/).map(Number);
    const [vx, vy, vw, vh] = vb.length === 4 && vb.every((n) => isFinite(n)) ? vb : [0, 0, 1000, 1000];
    clone.setAttribute('width', String(Math.round(vw)));
    clone.setAttribute('height', String(Math.round(vh)));

    const cssText = chartCss();
    if (!cssText) {
        /* Без стилей схема экспортируется чёрным силуэтом, и человек узнаёт об
           этом уже в скачанном файле. Молчать здесь нельзя: пустой результат
           выглядит как успех. */
        window.__aeFunnel && window.__aeFunnel('export_css_missing', {});
        window.__toast && window.__toast(t('export_failed', 'Не удалось собрать картинку — обновите страницу'), 'error');
        return null;
    }
    const cs = getComputedStyle(document.documentElement);
    const names = new Set(['--bg-primary']);          // фон подложки, в стилях схемы его нет
    let m;
    const re = /var\(\s*(--[\w-]+)/g;
    while ((m = re.exec(cssText))) names.add(m[1]);
    let varBlock = ':root{';
    for (const v of names) {
        const val = cs.getPropertyValue(v).trim();
        if (val) varBlock += v + ':' + val + ';';
    }
    varBlock += '}';

    const style = document.createElementNS('http://www.w3.org/2000/svg', 'style');
    style.textContent = varBlock + cssText;
    clone.insertBefore(style, clone.firstChild);
    const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    bg.setAttribute('x', String(vx)); bg.setAttribute('y', String(vy));
    bg.setAttribute('width', String(vw)); bg.setAttribute('height', String(vh));
    bg.setAttribute('fill', cs.getPropertyValue('--bg-primary').trim() || '#0e1116');
    clone.insertBefore(bg, style.nextSibling);
    return clone;
}

function fileBase() {
    const who = ($('dm-name') || {}).value || '';
    const dt = readDate();
    const parts = ['matrix'];
    if (who.trim()) parts.push(who.trim());
    if (dt) parts.push(dt.y + '-' + String(dt.mo).padStart(2, '0') + '-' + String(dt.d).padStart(2, '0'));
    const join = window.__outputName && window.__outputName.join;
    return join ? join(parts, '') : parts.join('-');
}

async function exportSvg() {
    const svg = standaloneSvg();
    if (!svg) return;
    const blob = new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml;charset=utf-8' });
    window.__downloadFile(blob, fileBase() + '.svg');
    window.__aeFunnel && window.__aeFunnel('export', { fmt: 'svg' });
}

async function exportPng() {
    const svg = standaloneSvg();
    if (!svg) return;
    const str = new XMLSerializer().serializeToString(svg);
    const url = URL.createObjectURL(new Blob([str], { type: 'image/svg+xml;charset=utf-8' }));
    try {
        const img = new Image();
        img.decoding = 'sync';
        await new Promise((res, rej) => {
            img.onload = res;
            img.onerror = () => rej(new Error('svg raster failed'));
            img.src = url;
        });
        const S = 2000;                       // 2× — чтобы числа читались в печати
        const cv = document.createElement('canvas');
        cv.width = S; cv.height = S;
        const ctx = cv.getContext('2d');
        ctx.drawImage(img, 0, 0, S, S);
        const blob = await new Promise((r) => cv.toBlob(r, 'image/png'));
        if (blob) window.__downloadFile(blob, fileBase() + '.png');
        window.__aeFunnel && window.__aeFunnel('export', { fmt: 'png' });
    } catch (e) {
        window.__handleError && window.__handleError(e, 'destiny-matrix png');
    } finally {
        URL.revokeObjectURL(url);
    }
}

/* ── сохранённые карты ────────────────────────────────────────────────── */

const STORE = 'dm.saved';
const loadSaved = () => {
    try { return JSON.parse(localStorage.getItem(STORE) || '[]'); } catch (e) { return []; }
};
const putSaved = (list) => {
    try { localStorage.setItem(STORE, JSON.stringify(list.slice(0, 30))); } catch (e) { /* приватное окно */ }
};

function saveCurrent() {
    const dt = readDate();
    if (!dt) return;
    const iso = dt.y + '-' + String(dt.mo).padStart(2, '0') + '-' + String(dt.d).padStart(2, '0');
    const name = (($('dm-name') || {}).value || '').trim();
    const list = loadSaved().filter((x) => !(x.date === iso && x.name === name));
    list.unshift({ date: iso, name: name });
    putSaved(list);
    renderSaved();
    window.__toast && window.__toast(t('saved_ok', 'Карта сохранена в этом браузере'), 'success');
    window.__aeFunnel && window.__aeFunnel('chart_saved', {});
}

function renderSaved() {
    const box = $('dm-saved');
    if (!box) return;
    const list = loadSaved();
    if (!list.length) { box.hidden = true; return; }
    box.hidden = false;
    box.innerHTML = '<h3>' + esc(t('saved_title', 'Сохранённые карты')) + '</h3><ul class="dm-saved-list">'
        + list.map((x, i) => '<li><button type="button" class="dm-saved-open" data-i="' + i + '">'
            + esc(x.name || t('saved_noname', 'Без имени')) + ' · ' + esc(x.date.split('-').reverse().join('.'))
            + '</button><button type="button" class="dm-saved-del" data-del="' + i + '" aria-label="'
            + esc(t('saved_delete', 'Удалить')) + '">×</button></li>').join('') + '</ul>';
}

/* ── старт ────────────────────────────────────────────────────────────── */

function wire() {
    const btn = $('dm-go');
    if (btn) btn.addEventListener('click', () => calc(true));
    const date = $('dm-date');
    /* Раньше здесь стояло `if (readDate()) calc(true)` — и это тихо ломало
       главное обещание экрана. Дата вне диапазона (1899, 2101) остаётся в
       поле, но до ветки с ошибкой не доходит: на экране продолжает висеть
       карта ПРЕДЫДУЩЕЙ даты, с её подписью. Человек уверен, что смотрит свою.
       Поймано на граничном прогоне 06.09. Теперь решает calc(): он либо
       считает, либо говорит, что не так, и убирает чужой результат. */
    if (date) date.addEventListener('change', () => calc(true));
    const name = $('dm-name');
    if (name) name.addEventListener('input', () => { if (CHART) calc(false); });
    const date2 = $('dm-date2');
    if (date2) date2.addEventListener('change', () => { if (CHART) calc(true); });

    const ring = $('dm-ring-toggle');
    if (ring) {
        /* На узком экране кольцо выключено ПО УМОЛЧАНИЮ. Оно даёт 64 подписи,
           которые на 358 px превращаются в кашу, и главное — держит холст
           1000×1000, при котором круглая фигура не помещается в телефон
           целиком ни разу. Явный выбор человека (сохранённая настройка ниже)
           сильнее этого умолчания. */
        if (window.matchMedia('(max-width: 620px)').matches) ring.checked = false;
        RING_ON = ring.checked;
        ring.addEventListener('change', () => {
            RING_ON = ring.checked;
            drawChart();
            window.__toolSettings && window.__toolSettings.save(SLUG, 'ring', RING_ON ? '1' : '0');
            window.__aeFunnel && window.__aeFunnel('ring_toggle', { on: RING_ON ? 1 : 0 });
        });
    }

    const pairAdd = $('dm-pair-add');
    if (pairAdd) pairAdd.addEventListener('toggle', () => {
        if (pairAdd.open) {
            window.__aeFunnel && window.__aeFunnel('pair_field_open', {});
            const f = $('dm-date2');
            if (f && !f.value) { try { f.focus({ preventScroll: true }); } catch (e) { f.focus(); } }
        }
    });

    /* Поворот телефона и изменение окна меняют обе величины сразу — вынос
       обязан пересчитываться, иначе после поворота схема либо не дотянется до
       края, либо вылезет за него. */
    let rt = 0;
    window.addEventListener('resize', () => {
        clearTimeout(rt);
        rt = setTimeout(fitChartBleed, 120);
    });

    const tl = document.querySelector('details.dm-panel');
    if (tl) tl.addEventListener('toggle', () => {
        if (tl.open) window.__aeFunnel && window.__aeFunnel('timeline_open', {});
    });

    const on = (id, fn) => { const e = $(id); if (e) e.addEventListener('click', fn); };
    on('dm-png', exportPng);
    on('dm-svg', exportSvg);
    on('dm-print', () => window.print());
    on('dm-save', saveCurrent);
    on('dm-copy', async () => {
        try {
            await navigator.clipboard.writeText(location.href);
            window.__toast && window.__toast(t('link_copied', 'Ссылка скопирована'), 'success');
        } catch (e) {
            window.__toast && window.__toast(t('link_failed', 'Скопируйте ссылку из адресной строки'), 'warning');
        }
    });

    const saved = $('dm-saved');
    if (saved) saved.addEventListener('click', (ev) => {
        const openBtn = ev.target.closest('.dm-saved-open');
        const delBtn = ev.target.closest('.dm-saved-del');
        const list = loadSaved();
        if (openBtn) {
            const x = list[+openBtn.dataset.i];
            if (!x) return;
            $('dm-date').value = x.date;
            $('dm-name').value = x.name || '';
            calc(true);
            $('dm-results').scrollIntoView({ block: 'nearest' });
        } else if (delBtn) {
            list.splice(+delBtn.dataset.del, 1);
            putSaved(list);
            renderSaved();
        }
    });
}

async function boot() {
    wire();
    renderSaved();
    await loadArcana();

    // предзаполнение: ?d= из ссылки, иначе сохранённая последняя дата
    let iso = null;
    try { iso = new URL(location.href).searchParams.get('d'); } catch (e) { }
    if (!iso && window.__toolSettings) iso = window.__toolSettings.load(SLUG, 'last', '') || null;
    let iso2 = null;
    try { iso2 = new URL(location.href).searchParams.get('d2'); } catch (e) { }
    if (iso2 && /^\d{4}-\d{2}-\d{2}$/.test(iso2) && $('dm-date2')) {
        $('dm-date2').value = iso2;
        /* Ссылка на пару обязана открыть тот блок, который её и заполняет:
           иначе вторая дата есть в расчёте, а поля с ней на экране нет — и
           убрать её человеку нечем. */
        const box = $('dm-pair-add');
        if (box) box.open = true;
    }
    if (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        $('dm-date').value = iso;
        calc(false);
    }
    // Явный выбор человека сильнее умолчания по ширине экрана — в ОБЕ стороны:
    // и «выключить на десктопе», и «включить на телефоне». Раньше здесь
    // обрабатывался только '0', и сохранённое «включено» молча не применялось.
    const ringPref = window.__toolSettings && window.__toolSettings.load(SLUG, 'ring', '');
    const ringEl = $('dm-ring-toggle');
    if (ringEl && (ringPref === '0' || ringPref === '1')) {
        const want = ringPref === '1';
        if (ringEl.checked !== want) {
            ringEl.checked = want; RING_ON = want; drawChart();
        }
    }
    const date = $('dm-date');
    if (date) date.addEventListener('change', () => {
        if (readDate() && window.__toolSettings) window.__toolSettings.save(SLUG, 'last', date.value);
    });
    window.__stage && window.__stage('ready');
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
